import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { OrgDepartment, OrgStructure } from '../../api/org';
import { Avatar } from '../../components/Avatar';
import { Icon } from '../../components/Icon';

const ZOOM_MAX = 1.6;
const plural = (n: number, one: string, few: string, many: string) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
};
const shortName = (name: string) => name.split(/\s+/).slice(0, 2).join(' ');

interface Props {
  structure: OrgStructure;
  byId: Map<string, OrgDepartment>;
  selectedId: string;
  expanded: Set<string>;
  /** Узел, который нужно показать в окне (после выбора из панели или поиска). */
  focusId: string | null;
  fullscreen: boolean;
  onSelect: (id: string) => void;
  onToggle: (id: string) => void;
  onFullscreen: () => void;
  onFocused: () => void;
}

/**
 * Схема сверху вниз уровнями. Перемещение и масштаб — через transform; схему нельзя увести
 * в пустоту: сверху она прижата к краю, по бокам — не дальше своих границ.
 */
export function OrgChart({
  structure,
  byId,
  selectedId,
  expanded,
  focusId,
  fullscreen,
  onSelect,
  onToggle,
  onFullscreen,
  onFocused,
}: Props) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const zoom = useRef({ s: 1, x: 0, y: 0, ready: false });
  const [level, setLevel] = useState(100);
  const drag = useRef<{ startX: number; startY: number; x: number; y: number; moved: boolean } | null>(null);
  const justDragged = useRef(false);
  const [dragging, setDragging] = useState(false);

  const minZoom = useCallback(() => {
    const canvas = canvasRef.current;
    const stage = stageRef.current;
    if (!canvas || !stage?.offsetWidth) return 0.1;
    const fit = Math.min(
      (canvas.clientWidth - 24) / stage.offsetWidth,
      (canvas.clientHeight - 84) / stage.offsetHeight,
    );
    return Math.max(0.1, Math.min(1, fit));
  }, []);

  const apply = useCallback(() => {
    const canvas = canvasRef.current;
    const stage = stageRef.current;
    if (!canvas || !stage) return;
    if (canvas.scrollLeft || canvas.scrollTop) {
      canvas.scrollLeft = 0;
      canvas.scrollTop = 0;
    }
    const z = zoom.current;
    z.s = Math.min(ZOOM_MAX, Math.max(minZoom(), z.s));
    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;
    const w = stage.offsetWidth * z.s;
    const h = stage.offsetHeight * z.s;
    z.x = w <= cw ? Math.round((cw - w) / 2) : Math.min(40, Math.max(cw - w - 40, z.x));
    z.y = h <= ch - 72 ? 0 : Math.min(0, Math.max(ch - h - 72, z.y));
    stage.style.transform = `translate(${z.x}px, ${z.y}px) scale(${z.s})`;
    setLevel(Math.round(z.s * 100));
  }, [minZoom]);

  const centerOn = useCallback(
    (id: string, top = 24) => {
      const canvas = canvasRef.current;
      const stage = stageRef.current;
      const node = stage?.querySelector<HTMLElement>(`[data-node="${id}"]`);
      if (!canvas || !stage || !node) return;
      const z = zoom.current;
      const stageBox = stage.getBoundingClientRect();
      const box = node.getBoundingClientRect();
      const x = (box.left - stageBox.left) / z.s;
      const y = (box.top - stageBox.top) / z.s;
      z.x = Math.round(canvas.clientWidth / 2 - (x + box.width / z.s / 2) * z.s);
      z.y = Math.round(top - y * z.s);
      apply();
    },
    [apply],
  );

  const zoomAt = useCallback(
    (next: number, cx: number, cy: number) => {
      const z = zoom.current;
      const scale = Math.min(ZOOM_MAX, Math.max(minZoom(), Math.round(next * 100) / 100));
      z.x = Math.round(cx - ((cx - z.x) * scale) / z.s);
      z.y = Math.round(cy - ((cy - z.y) * scale) / z.s);
      z.s = scale;
      apply();
    },
    [apply, minZoom],
  );

  const fitAll = useCallback(() => {
    const canvas = canvasRef.current;
    const stage = stageRef.current;
    if (!canvas || !stage) return;
    const z = zoom.current;
    z.s = Math.min(
      1,
      (canvas.clientWidth - 24) / stage.scrollWidth,
      (canvas.clientHeight - 70) / stage.scrollHeight,
    );
    z.x = Math.round((canvas.clientWidth - stage.scrollWidth * z.s) / 2);
    z.y = 0;
    apply();
  }, [apply]);

  // После каждой отрисовки: первый показ — корень по центру; затем — запрошенный узел.
  useLayoutEffect(() => {
    if (!zoom.current.ready) {
      zoom.current.ready = true;
      apply();
      const root = structure.rootIds[0];
      if (root) centerOn(root, 20);
      return;
    }
    if (focusId) {
      centerOn(focusId);
      onFocused();
    } else {
      apply();
    }
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = canvas.getBoundingClientRect();
      if (event.ctrlKey || event.metaKey) {
        zoomAt(
          zoom.current.s * (event.deltaY < 0 ? 1.1 : 0.9),
          event.clientX - rect.left,
          event.clientY - rect.top,
        );
      } else {
        zoom.current.x -= event.deltaX;
        zoom.current.y -= event.deltaY;
        apply();
      }
    };
    const onResize = () => apply();
    canvas.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('resize', onResize);
    return () => {
      canvas.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', onResize);
    };
  }, [apply, zoomAt]);

  const onPointerDown = (event: React.PointerEvent) => {
    if (event.button !== 0 || (event.target as HTMLElement).closest('.td-zoom')) return;
    drag.current = {
      startX: event.clientX,
      startY: event.clientY,
      x: zoom.current.x,
      y: zoom.current.y,
      moved: false,
    };
    const move = (e: PointerEvent) => {
      const d = drag.current;
      if (!d) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      if (!d.moved && Math.abs(dx) + Math.abs(dy) < 5) return;
      d.moved = true;
      setDragging(true);
      zoom.current.x = d.x + dx;
      zoom.current.y = d.y + dy;
      apply();
    };
    const up = () => {
      if (drag.current?.moved) justDragged.current = true;
      drag.current = null;
      setDragging(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // Клик после перетаскивания не выбирает узел.
  const onClickCapture = (event: React.MouseEvent) => {
    if (justDragged.current) {
      justDragged.current = false;
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const renderNode = (id: string) => {
    const department = byId.get(id);
    if (!department) return null;
    const isRoot = structure.rootIds.includes(id);
    const open = department.childIds.length > 0 && expanded.has(id);
    const heads = department.heads
      .map((head) => structure.people[head.id])
      .filter((person) => person !== undefined);
    const selected = selectedId === id;
    return (
      <li key={id}>
        <div
          className={`ot-node${selected ? ' selected' : ''}${heads.length ? '' : ' no-head'}${isRoot ? ' root' : ''}`}
          data-node={id}
        >
          <button type="button" className="ot-main" aria-pressed={selected} onClick={() => onSelect(id)}>
            <span className="ot-name">{department.name}</span>
            {heads.length ? (
              heads.map((head) => (
                <span key={head.id} className="ot-head">
                  <Avatar name={head.fullName} photoUrl={head.photoUrl} />
                  <span className="ot-head-text">
                    <b>{shortName(head.fullName)}</b>
                    <small title={head.position ?? ''}>{head.position ?? 'Руководитель'}</small>
                  </span>
                </span>
              ))
            ) : (
              <span className="status status-bad">Нет руководителя</span>
            )}
            <span className="ot-meta">
              <Icon name="users" size={13} />
              <b>{department.totalCount}</b>
              {department.childIds.length ? (
                <>
                  <span className="ot-sep" />
                  {department.childIds.length}{' '}
                  {plural(department.childIds.length, 'отдел', 'отдела', 'отделов')}
                </>
              ) : null}
            </span>
          </button>
          {department.childIds.length ? (
            <button
              type="button"
              className="ot-toggle"
              aria-expanded={open}
              aria-label={`${open ? 'Свернуть' : 'Раскрыть'} «${department.name}»`}
              onClick={() => onToggle(id)}
            >
              <Icon name={open ? 'minus' : 'plus'} size={13} />
            </button>
          ) : null}
        </div>
        {open ? <ul>{department.childIds.map(renderNode)}</ul> : null}
      </li>
    );
  };

  const center = () => {
    const canvas = canvasRef.current;
    return canvas ? [canvas.clientWidth / 2, canvas.clientHeight / 2] : [0, 0];
  };

  return (
    <section className="panel org-canvas-panel" aria-label="Схема оргструктуры">
      <div
        ref={canvasRef}
        className={`td-canvas${dragging ? ' dragging' : ''}`}
        onPointerDown={onPointerDown}
        onClickCapture={onClickCapture}
      >
        <div ref={stageRef} className="td-stage">
          <ul className="td-tree">{structure.rootIds.map(renderNode)}</ul>
        </div>
        <fieldset className="td-zoom">
          <legend className="visually-hidden">Масштаб</legend>
          <button
            type="button"
            aria-label="Уменьшить"
            onClick={() => zoomAt(zoom.current.s / 1.2, ...(center() as [number, number]))}
          >
            <Icon name="minus" size={14} />
          </button>
          <button
            type="button"
            aria-label="Масштаб 100 %"
            onClick={() => zoomAt(1, ...(center() as [number, number]))}
          >
            {level} %
          </button>
          <button
            type="button"
            aria-label="Увеличить"
            onClick={() => zoomAt(zoom.current.s * 1.2, ...(center() as [number, number]))}
          >
            <Icon name="plus" size={14} />
          </button>
          <button type="button" onClick={fitAll}>
            Вписать
          </button>
          <button
            type="button"
            aria-pressed={fullscreen}
            aria-label={fullscreen ? 'Свернуть' : 'Во весь экран'}
            onClick={onFullscreen}
          >
            <Icon name={fullscreen ? 'minimize' : 'maximize'} size={14} />
            <span className="zoom-label">{fullscreen ? 'Свернуть' : 'Во весь экран'}</span>
          </button>
        </fieldset>
      </div>
    </section>
  );
}
